import { useState, useEffect, useCallback } from 'react';
import { Shield, UserPlus, ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// iter820 — Manage platform super admins: promote existing users, create new
// ones, and revoke (self / last-admin protected on the backend).
export function SuperAdminsView() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/super-admins');
      setAdmins(r.data?.super_admins || []);
    } catch { toast.error('Failed to load super admins'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const promote = async () => {
    if (!promoteEmail.trim()) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/super-admins/promote', { email: promoteEmail.trim() });
      toast.success(r.data?.message || 'User promoted');
      setPromoteEmail('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Promote failed'); }
    setBusy(false);
  };

  const create = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error('Name, email and password are required'); return;
    }
    setBusy(true);
    try {
      const r = await api.post('/admin/super-admins/create', newAdmin);
      toast.success(r.data?.message || 'Super admin created');
      setNewAdmin({ name: '', email: '', password: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    setBusy(false);
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revoke super-admin access from ${u.name || u.email}?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/super-admins/${u.id}/revoke`);
      toast.success(r.data?.message || 'Access revoked');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Revoke failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="super-admins-view">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Promote existing */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck size={16} className="text-emerald-600" /> Promote an existing user</div>
            <p className="text-xs text-muted-foreground">Enter the email of an existing user to grant them full platform (all-companies) super-admin access.</p>
            <div className="flex gap-2">
              <Input type="email" placeholder="user@company.com" value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)} className="h-9" data-testid="promote-email-input" />
              <Button onClick={promote} disabled={busy || !promoteEmail.trim()} className="h-9 shrink-0" data-testid="promote-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} className="mr-1" />Promote</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create new */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><UserPlus size={16} className="text-blue-600" /> Create a new super admin</div>
            <div className="grid grid-cols-1 gap-2">
              <div><Label className="text-xs">Full name</Label><Input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} className="h-9" data-testid="new-sa-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} className="h-9" data-testid="new-sa-email" /></div>
              <div><Label className="text-xs">Password (min 8 chars)</Label><Input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} className="h-9" data-testid="new-sa-password" /></div>
            </div>
            <Button onClick={create} disabled={busy} className="h-9 w-full" data-testid="create-sa-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <><UserPlus size={14} className="mr-1" />Create Super Admin</>}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Current super admins */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Shield size={16} className="text-primary" /> Current Super Admins ({admins.length})</div>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="divide-y divide-border" data-testid="super-admins-list">
              {admins.map((u) => (
                <div key={u.id} className="flex items-center gap-3 py-2.5" data-testid={`sa-row-${u.id}`}>
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {u.name || '—'}
                      {u.is_self && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">You</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[11px] text-error border-error/40 hover:bg-error/10 shrink-0"
                    disabled={busy || u.is_self || admins.length <= 1}
                    title={u.is_self ? "You can't revoke your own access" : admins.length <= 1 ? 'Cannot revoke the last super admin' : 'Revoke super-admin access'}
                    onClick={() => revoke(u)}
                    data-testid={`revoke-sa-${u.id}`}
                  >
                    <ShieldOff size={12} className="mr-1" />Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
